import { getContentstackImagesForWidths } from '@noths/polaris-client-utils';
import parse from 'html-react-parser';
import dynamic from 'next/dynamic';

import { HeroBannerProps, TextColorMode } from 'src/components/blocks/HeroBannerBlock';
import { trackImpression, trackLinkClick } from 'src/tracking/common/banner';
import { ContentBlockRenderer } from '../../../types/Block';
import { ContentstackImage, ContentstackSection } from '../../../types/Contentstack';
import { Transformer } from '../../../types/Transformer';

export interface ContentstackHeroBanner extends ContentstackSection {
  background_color: string;
  text_color_mode: TextColorMode;
  image: {
    image_source: ContentstackImage;
    alt_text: string;
  };
  heading: {
    line1: string;
    line2?: string;
    underline: {
      show_underline: boolean;
      underline_color: string;
    };
  };
  sub_heading?: string;
  copy?: string;
  button?: {
    background_color: string;
    text: string;
    text_color: TextColorMode;
    url: string;
  };
  tracking_title: string;
}

interface HeroBannerSerializedData extends Omit<HeroBannerProps, 'subHeading' | 'copy'> {
  trackingTitle: string;
  subHeading: string;
  copy: string;
}

const parseOptionalHtmlString = (html: string) => {
  const reactNodes = parse(html);
  return !Array.isArray(reactNodes) ? reactNodes : undefined;
};

/* istanbul ignore next */
export const renderer: ContentBlockRenderer<HeroBannerSerializedData, HeroBannerProps> = {
  Component: dynamic<HeroBannerProps>(() =>
    import('src/components/blocks/HeroBannerBlock').then((mod) => mod.HeroBannerBlock),
  ),
  getProps: ({ ...serializedData }) => {
    const { trackingPosition, trackingTitle, subHeading, copy, ...restOfSerializedData } =
      serializedData;

    const placementContext = {
      trackingPosition,
      trackingTitle,
    };

    return {
      subHeading: parseOptionalHtmlString(serializedData.subHeading),
      copy: parseOptionalHtmlString(serializedData.copy),
      onLinkClick: (e: React.MouseEvent) =>
        trackLinkClick(e, placementContext, 'Hero banner', 'Link'),
      onButtonClick: (e: React.MouseEvent<HTMLAnchorElement>) =>
        trackLinkClick(e, placementContext, 'Hero banner', 'CTA'),
      onVisible: () => trackImpression(placementContext, 'Hero banner'),
      ...restOfSerializedData,
    };
  },
};

export const transformer: Transformer<
  ContentstackHeroBanner,
  HeroBannerSerializedData,
  HeroBannerProps
> = ({
  _metadata,
  background_color,
  text_color_mode,
  image,
  heading,
  sub_heading,
  copy,
  button,
  tracking_title,
}) => ({
  uid: _metadata.uid,
  renderer,
  serializedData: {
    backgroundColor: background_color,
    images: getContentstackImagesForWidths(image.image_source.url, [360, 500, 700, 900, 1132], {
      aspectRatio: '16:9',
    }),
    imageWidth: 360,
    imageHeight: 216,
    imageAlt: image.alt_text,
    textColorMode: text_color_mode,
    headingLine1: heading.line1,
    headingLine2: heading.line2,
    ...(heading.underline.show_underline && {
      headingUnderlineColor: heading.underline.underline_color,
    }),
    subHeading: sub_heading || '',
    copy: copy || '',
    buttonBackgroundColor: button?.background_color || '',
    buttonHref: button?.url || '',
    buttonText: button?.text || '',
    buttonTextColorMode: button?.text_color || 'light',
    trackingTitle: tracking_title,
  },
});
